import { useTranslation } from 'react-i18next';

import CourseLessonsHeader from '@/components/course-progress/CourseLessonsHeader';
import CoursePathSelect from '@/components/course-path/CoursePathSelect';
import KlassSelect from '@/components/klass/KlassSelect';
import EmptyMessage from '@/components/shared/EmptyMessage';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import useShellBackHref from '@/data/hook/app-shell/useShellBackHref';
import useStaffCourseSelection from '@/data/hook/course-path/useStaffCourseSelection';
import useAuth from '@/data/hook/useAuth';
import { UserTypeEnum } from '@/models/User';
import { CourseRoute, ROUTES } from '@/utils/routes';
import StaffDefaultKlassRedirect from './StaffDefaultKlassRedirect';
import StaffUnitSection from './StaffUnitSection';
import UnitList from './UnitList';

const KLASS_SELECT_ROLES = [
  UserTypeEnum.SUPER_ADMIN,
  UserTypeEnum.UNIT_ADMIN,
  UserTypeEnum.TEACHER,
];

export default function StaffCourseLessonsView() {
  const { user } = useAuth();
  const { slug, klassId, shouldSelectDefaultKlass, selectCourse, selectKlass } =
    useStaffCourseSelection();
  const canSelectKlass = !!user && KLASS_SELECT_ROLES.includes(user.userType);

  const homeUrl = ROUTES.DASHBOARD.BASE;
  useShellBackHref(homeUrl);

  return (
    <div className="flex flex-col shrink-0 gap-4 min-h-full">
      <CourseLessonsHeader
        backHref={homeUrl}
        actions={
          <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
            <CoursePathSelect
              value={slug}
              onChange={selectCourse}
              className="md:w-64"
            />
            <ConditionalRenderer condition={canSelectKlass}>
              <KlassSelect
                value={klassId ? Number(klassId) : undefined}
                onChange={selectKlass}
                className="md:w-56"
              />
            </ConditionalRenderer>
          </div>
        }
      />
      <section className="flex flex-1 flex-col gap-6 rounded-2xl">
        <StaffCourseLessonsContent
          slug={slug}
          klassId={klassId}
          shouldSelectDefaultKlass={canSelectKlass && shouldSelectDefaultKlass}
        />
      </section>
    </div>
  );
}

type StaffCourseLessonsContentProps = Partial<CourseRoute> & {
  shouldSelectDefaultKlass: boolean;
};

function StaffCourseLessonsContent({
  slug,
  klassId,
  shouldSelectDefaultKlass,
}: StaffCourseLessonsContentProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLessons.staff',
  });

  if (shouldSelectDefaultKlass) return <StaffDefaultKlassRedirect />;

  if (!slug) return <EmptyMessage text={t('selectCourseOrKlass')} />;

  return (
    <UnitList slug={slug}>
      {(unit, index) => (
        <StaffUnitSection
          key={unit.id}
          unit={unit}
          defaultOpen={index === 0}
          slug={slug}
          klassId={klassId}
        />
      )}
    </UnitList>
  );
}
